import React, { useState } from 'react';
import ReactHlsPlayer from 'react-hls-player';
import { Grid, Card } from '@material-ui/core';
import MyUrlPublic from "../fields/public-url";
import {
  Edit,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  ReferenceInput,
  ArrayInput,
  AutocompleteInput,
  SelectInput,
  FileField,
  FileInput,
  FormDataConsumer,
  BooleanInput,
  required,
  maxLength,
  number,
} from 'react-admin';
import Toolbar from '../resource-toolbar';
import WithTooltip from '../with-tooltip';
import { CategoryInput } from '../inputs';
import { LanguageChanger } from '../fields';
import { useServerValidation, useLanguageChange, getStyle } from '../../helpers';
import { PROD_STATUSES, LANGUAGES, LICENSE_TYPES, PRICE_TIER } from '../../constants';

const Title = ({ record }) => `Artwork #${record.id}. ${record.name}`;

const WithProps = ({ children, ...props }) => children(props);

export default (props) => {
  const [record, setRecord] = useState(null);
  const { errors, onFailure } = useServerValidation();
  const [defaultLanguage] = LANGUAGES;
  const { language, onLanguageChange } = useLanguageChange(defaultLanguage);
  return (
    <Grid container spacing={2}>
      <Grid item lg={6} md={8} xs={12}>
        <Edit {...props} onFailure={onFailure} undoable={false} title={<Title />}>
          <SimpleForm toolbar={<Toolbar resourceName="products" />}>
            <WithProps>
              {({ record }) => {
                setRecord(record);
                return null;
              }}
            </WithProps>
            <TextInput source="id" fullWidth disabled />
            {(props.permissions === 'MASTER_ADMIN' || props.permissions === 'ADMIN') && (
              <>
                <ReferenceInput
                  source="provider_id"
                  reference="users"
                  filter={{ role_id: [0,1,2] }}
                  sort={{ field: 'first_name', order: 'ASC' }}
                  fullWidth
                  validate={required()}
                >
                  <AutocompleteInput optionText={(record) => record.id ? `${record.first_name} ${record.last_name}` : ''} />
                </ReferenceInput>
                <SelectInput
                  source="status"
                  translateChoice={false}
                  choices={PROD_STATUSES}
                  fullWidth
                  defaultValue="New"
                  validate={required()}
                />
                <SelectInput
                  source="license_type"
                  translateChoice={false}
                  choices={LICENSE_TYPES}
                  fullWidth
                  defaultValue="Paid"
                  validate={required()}
                />
              </>
            )}
            <ReferenceInput
              source="host_id"
              reference="hosts"
              fullWidth
              sort={{ field: 'name', order: 'ASC' }}
              perPage={1000}
            >
              <AutocompleteInput optionText="name" />
            </ReferenceInput>
            <ReferenceInput
              source="artist_id"
              reference="artists"
              fullWidth
              validate={required()}
              sort={{ field: 'first_name', order: 'ASC' }}
              perPage={1000}
            >
              <AutocompleteInput optionText={(record) => record.id ? `${record.first_name} ${record.last_name}` : ''} />
            </ReferenceInput>
            <TextInput source="name" label="Artwork Name" fullWidth validate={[required(), maxLength(255)]} />
            <WithTooltip tooltipText="Allowed formats: JPG, PNG, MP4, AVI, M4V, WEBM">
              <FileInput source="files" label="Media File" accept="video/*, image/*">
                <FileField source="url" title="Artwork Media File" />
              </FileInput>
            </WithTooltip>
            <ReferenceInput source="art_type_id" reference="art-types" fullWidth>
              <SelectInput source="name" validate={required()} />
            </ReferenceInput>
            <ArrayInput source="categories" validate={required()}>
              <SimpleFormIterator>
                <FormDataConsumer>
                  {({ formData, getSource, ...rest }) => {
                    return (
                      <CategoryInput
                        source={getSource('categories')}
                        getSource={getSource}
                        formData={formData}
                        errors={errors}
                        validate={required()}
                        {...rest}
                      />
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
            <TextInput source="location" fullWidth />
            <TextInput source="year" fullWidth validate={number()} />
            {(props.permissions === 'MASTER_ADMIN' || props.permissions === 'ADMIN') && (
              <>
                <SelectInput
                  source="price_tier"
                  translateChoice={false}
                  choices={PRICE_TIER}
                  fullWidth
                />
                <BooleanInput source="wall" defaultValue={true} />
                <BooleanInput source="staff_pick" />
              </>
            )}
            <LanguageChanger language={language} onLanguageChange={onLanguageChange} />
            <TextInput style={getStyle(language, '_en')} rows="5" multiline source="description" fullWidth validate={maxLength(10000)} />
            <TextInput style={getStyle(language, '_fr')} rows="5" multiline source="description_fr" fullWidth validate={maxLength(10000)} />
            <TextInput style={getStyle(language, '_jp')} rows="5" multiline source="description_jp" fullWidth validate={maxLength(10000)} />
            <TextInput style={getStyle(language, '_cn')} rows="5" multiline source="description_cn" fullWidth validate={maxLength(10000)} />
            <TextInput style={getStyle(language, '_de')} rows="5" multiline source="description_de" fullWidth validate={maxLength(10000)} />
            <TextInput style={getStyle(language, '_kr')} rows="5" multiline source="description_kr" fullWidth validate={maxLength(10000)} />
            <TextInput style={getStyle(language, '_es')} rows="5" multiline source="description_es" fullWidth validate={maxLength(10000)} />
            <WithTooltip tooltipText="Tags separated by comma (,)">
              <TextInput source="tags" fullWidth />
            </WithTooltip>
          </SimpleForm>
        </Edit>
      </Grid>
      <Grid item lg={6} md={4} xs={12}>
        {record && (
          <Card style={{ marginBottom: '2.5em', marginTop: '1em' }}>
            {
              (record.url || record.preview_img_url || record.original_preview_url) ? (
                record.is_image ?
                  <img src={record.url || record.preview_img_url || record.original_preview_url} width="100%" alt="Preview" />
                  : <ReactHlsPlayer url={record.url || record.preview_img_url} autoplay={false} controls={true} width="100%" />

                ) 
              : null
            }
          </Card>
        )}
        {record &&(
            <Edit {...props}>
                <MyUrlPublic></MyUrlPublic>
            </Edit>
        )}
      </Grid>
    </Grid>
  );
};
